import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  X,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Inbox
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Diagnóstico de una conexión de Meta.
 *
 * Existe porque "a veces no llegan los mensajes" no es accionable. Esta
 * pantalla lo parte en las tres cosas que pueden estar pasando y dice cuál es:
 *
 *  1. Meta no los manda        → la suscripción está mal.
 *  2. Meta los manda y se pierden acá → hay eventos sin procesar (y se pueden
 *     reprocesar, porque el payload quedó guardado antes de acusar recibo).
 *  3. Salen y no llegan al cliente → lo dice la comparación con Meta.
 */

const fmt = (value) =>
  value ? new Date(value).toLocaleString() : "nunca";

const sinceHours = (value) => {
  if (!value) return null;
  return Math.floor((Date.now() - new Date(value).getTime()) / 3600000);
};

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-1.5">
    <span className="text-xs text-slate-500 dark:text-slate-400 flex-shrink-0">{label}</span>
    <span className="text-xs text-slate-800 dark:text-slate-200 text-right break-words">{children}</span>
  </div>
);

const Section = ({ title, tone = "neutral", children }) => {
  const tones = {
    neutral: "border-slate-200 dark:border-slate-700",
    ok: "border-emerald-300 dark:border-emerald-800",
    warn: "border-amber-300 dark:border-amber-800",
    bad: "border-red-300 dark:border-red-800"
  };
  const Icon = tone === "ok" ? CheckCircle2 : tone === "bad" ? XCircle : tone === "warn" ? AlertTriangle : Inbox;
  const iconColor =
    tone === "ok" ? "text-emerald-600 dark:text-emerald-400"
      : tone === "bad" ? "text-red-600 dark:text-red-400"
        : tone === "warn" ? "text-amber-600 dark:text-amber-400"
          : "text-slate-400";

  return (
    <div className={`border rounded-xl p-3.5 ${tones[tone]}`}>
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        {title}
      </h3>
      {children}
    </div>
  );
};

const MetaDiagnosticsModal = ({ open, onClose, connection }) => {
  const [loading, setLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!connection?.id) return;
    setLoading(true);
    try {
      const { data: result } = await api.get(`/api/meta/diagnostics/${connection.id}`);
      setData(result);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [connection?.id]);

  useEffect(() => {
    if (open) load();
    else setData(null);
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      const { data: result } = await api.post("/api/meta/webhook-events/reprocess-pending");
      if (result.recovered > 0) {
        toast.success(`${result.recovered} webhook(s) reprocesados.`);
      } else {
        toast.info("No había webhooks pendientes de reprocesar.");
      }
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setReprocessing(false);
    }
  };

  const inbox = data?.inbox;
  const sub = data?.subscription;
  const rec = data?.reconciliation;

  const seesMessages = !!inbox?.fieldsSeen?.includes("messages");
  const stuck = (inbox?.counts?.pending || 0) + (inbox?.counts?.failed || 0);
  const lastHours = sinceHours(inbox?.lastEventAt);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Diagnóstico de {connection?.name}
            </h2>
            <p className="text-[11px] text-slate-400">Últimos 7 días</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={load}
              disabled={loading}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
              title="Volver a consultar"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex flex-col gap-3">
          {loading && !data && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Consultando a Meta…
            </div>
          )}

          {data && (
            <>
              <Section
                title="1 · ¿Meta está mandando los mensajes?"
                tone={sub?.ok && seesMessages ? "ok" : "bad"}
              >
                <Row label="Último webhook recibido">
                  {fmt(inbox.lastEventAt)}
                  {lastHours !== null && lastHours > 24 && (
                    <span className="text-amber-600 dark:text-amber-400"> (hace {lastHours} h)</span>
                  )}
                </Row>
                <Row label="Último con el campo messages">{fmt(inbox.lastMessagesFieldAt)}</Row>
                <Row label="Campos recibidos">
                  {inbox.fieldsSeen.length > 0 ? inbox.fieldsSeen.join(", ") : "ninguno"}
                </Row>
                <Row label="Apps suscritas según Meta">
                  {sub.ok
                    ? (sub.subscribedApps || []).map(a => a.name || a.id).join(", ") || "—"
                    : <span className="text-red-600 dark:text-red-400">{sub.error}</span>}
                </Row>
                {sub.requiredDashboardFields && (
                  <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                    Estos campos hay que marcarlos <b>a mano</b> en el App Dashboard de Meta
                    (WhatsApp → Configuration → Webhooks). La API no puede activarlos:
                    <span className="font-mono"> {sub.requiredDashboardFields.join(", ")}</span>
                  </p>
                )}
                {sub.managedFields && (
                  <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                    Campos que la plataforma suscribe sola:
                    <span className="font-mono"> {sub.managedFields}</span>
                  </p>
                )}
                {!seesMessages && inbox.fieldsSeen.length > 0 && (
                  <p className="mt-2 text-[11px] text-red-600 dark:text-red-400 leading-relaxed">
                    Llegan webhooks pero <b>ninguno con el campo <span className="font-mono">messages</span></b>:
                    el problema está en la suscripción, no en la plataforma.
                  </p>
                )}
              </Section>

              <Section
                title="2 · ¿Se está perdiendo algo aquí?"
                tone={stuck > 0 ? "warn" : "ok"}
              >
                <Row label="Webhooks procesados">{inbox.counts.done}</Row>
                <Row label="Sin procesar (se cortaron a medias)">{inbox.counts.pending}</Row>
                <Row label="Fallidos">{inbox.counts.failed}</Row>
                {stuck > 0 && (
                  <>
                    <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-400 leading-relaxed">
                      El contenido de estos webhooks sigue guardado: se puede reprocesar sin
                      perder nada. Los mensajes aparecerán en su conversación.
                    </p>
                    <button
                      onClick={handleReprocess}
                      disabled={reprocessing}
                      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                    >
                      {reprocessing
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <RefreshCw className="w-3.5 h-3.5" />}
                      Reprocesar pendientes
                    </button>
                  </>
                )}
                {inbox.recentProblems.length > 0 && (
                  <div className="mt-3 border-t border-slate-200 dark:border-slate-700 pt-2 flex flex-col gap-1">
                    {inbox.recentProblems.slice(0, 6).map(p => (
                      <div key={p.id} className="text-[11px] text-slate-500 dark:text-slate-400">
                        <span className="font-mono">#{p.id}</span> · {p.status} · {p.fields || "?"} ·{" "}
                        {new Date(p.createdAt).toLocaleString()}
                        {p.error && <span className="text-red-500"> · {p.error.slice(0, 120)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              <Section
                title="3 · ¿Nuestros envíos llegan al cliente?"
                tone={rec?.ok ? (rec.notes?.length > 1 ? "warn" : "ok") : "neutral"}
              >
                <Row label="Enviados según la plataforma">{rec.ourOutgoing}</Row>
                <Row label="Aceptados por Meta">{rec.ok ? rec.metaSent : "—"}</Row>
                <Row label="Entregados al cliente">{rec.ok ? rec.metaDelivered : "—"}</Row>
                <Row label="Entrantes guardados">{rec.ourIncoming}</Row>
                <Row label="Webhooks de mensajes recibidos">{rec.inboundEvents}</Row>
                {!rec.ok && rec.error && (
                  <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">{rec.error}</p>
                )}
                {(rec.notes || []).map((note, i) => (
                  <p key={i} className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                    {note}
                  </p>
                ))}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetaDiagnosticsModal;
